import { ref, onValue } from 'firebase/database';
import db from './firebase'; // Import de l'instance Firebase

/**
 * Fonction pour formater les donnÃ©es en attribuant chaque valeur Ã  un champ spÃ©cifique
 * @param {string} rawPoint - Une chaÃ®ne contenant les donnÃ©es brutes
 * @returns {object} - Un objet formatÃ© avec les champs correspondants
 */
const formatPoint = (rawPoint) => {
  const [id, Ah, V, A, S, D, Deg, RPM, HW, Nm, ThI, ThO, AuxA, AuxD, Flgs] = rawPoint
    .split(/\s+/) // SÃ©parer par espaces
    .map(Number); // Convertir en nombres

  return {
    id, // Extraire l'ID
    Ah,
    V,
    A,
    S,
    D,
    Deg,
    RPM,
    HW,
    Nm,
    ThI,
    ThO,
    AuxA,
    AuxD,
    Flgs
  };
};

/**
 * Fonction pour rÃ©cupÃ©rer les donnÃ©es en temps rÃ©el depuis Firebase
 * @param {string} collectionName - Le chemin de la collection Firebase
 * @param {function} onSuccess - Callback pour gÃ©rer les donnÃ©es rÃ©cupÃ©rÃ©es
 * @param {function} onError - Callback pour gÃ©rer les erreurs
 * @returns {function} unsubscribe - Fonction pour annuler l'Ã©coute
 */
export const fetchDataFromFirebaseCycle = (collectionName, onSuccess, onError) => {
  const pointsRef = ref(db, collectionName);

  const unsubscribe = onValue(
    pointsRef,
    (snapshot) => {
      const fetchedData = [];
      snapshot.forEach((childSnapshot) => {
        const rawPoint = childSnapshot.val().cycle_analyst; // Données brutes
        if (rawPoint !== undefined) {
          const formattedPoint = formatPoint(rawPoint); // Formatter le point
          // verifier si le point formater a bien 15 champs remplis
          if (Object.values(formattedPoint).filter((value) => value !== undefined && !isNaN(value)).length >= 11) {
            if (fetchedData.length === 0 ) {
              fetchedData.push(formattedPoint);
            }
            else {
              const lastPoint = fetchedData[fetchedData.length - 1];
              // verifier que la difference entre chaque valeur ne depasse pas 10, a part pour l'id
              if (Math.abs(lastPoint['Ah'] - formattedPoint['Ah']) < 10 ) {
                fetchedData.push(formattedPoint);
              }
            }
            
          }
        }
      });
      onSuccess(fetchedData);
    },
    (error) => {
      if (onError) onError(error);
    }
  );

  return unsubscribe; // Permet d'arrÃªter l'Ã©coute
};

export const fetchDataFromFirebaseGPS = (collectionName, onSuccess, onError) => {
  const pointsRef = ref(db, collectionName);

  const unsubscribe = onValue(
    pointsRef,
    (snapshot) => {
      const fetchedData = [];
      snapshot.forEach((childSnapshot) => {
        const data = childSnapshot.val();
        const rawPoint = data?.gps || null;
        if (rawPoint) {
          fetchedData.push(rawPoint);
        }
      });

      const formattedGPSData = formatGPS(fetchedData.filter(Boolean));

      onSuccess(formattedGPSData);
    },
    (error) => {
      console.error("Erreur Firebase GPS:", error);
      if (onError) onError(error);
    }
  );

  return unsubscribe;
};


/**
 * Récupère les points depuis Firebase et calcule la moyenne d'une propriété spécifique.
 * @param {string} collectionName - Nom de la collection dans Firebase.
 * @param {string} property - Propriété à utiliser pour calculer la moyenne (ex: 'V').
 * @param {function} onSuccess - Callback pour gérer les données récupérées.
 * @param {function} onError - Callback pour gérer les erreurs (optionnel).
 */
export const fetchAverage = (collectionName, property, onSuccess, onError) => {
  const pointsRef = ref(db, collectionName);
  const unsubscribe = onValue(
    pointsRef,
    (snapshot) => {
      const fetchedData = [];
      snapshot.forEach((childSnapshot) => {
        const rawPoint = childSnapshot.val().cycle_analyst; // Données brutes
        if (rawPoint !== undefined) {
          const formattedPoint = formatPoint(rawPoint); // Formatter le point
          // verifier si le point formater a bien 15 champs remplis
          if (Object.values(formattedPoint).filter((value) => value !== undefined && !isNaN(value)).length >= 11) {
            if (fetchedData.length === 0 ) {
              fetchedData.push(formattedPoint);
            }
            else {
              const lastPoint = fetchedData[fetchedData.length - 1];
              // verifier que la difference entre chaque valeur ne depasse pas 10, a part pour l'id
              if (Math.abs(lastPoint['Ah'] - formattedPoint['Ah']) < 10 ) {
                fetchedData.push(formattedPoint);
              }
            }
            
          }
          
        }
      });

      if (fetchedData.length > 0) {
        const average =
          fetchedData.reduce((acc, point) => acc + (point[property] || 0), 0) /
          fetchedData.length;
        onSuccess(average);
      }
    },
    (error) => {
      console.error('Erreur lors de la récupération des données :', error);
      if (onError) onError(error);
    }
  );

  return unsubscribe;
};

/**
 * Récupère le dernier point depuis Firebase.
 * @param {string} collectionName - Nom de la collection dans Firebase.
 * @param {function} onSuccess - Callback pour gérer le dernier point récupéré.
 * @param {function} onError - Callback pour gérer les erreurs (optionnel).
 */
export const fetchLastPoint = (collectionName, onSuccess, onError) => {
  const pointsRef = ref(db, collectionName);
  const unsubscribe = onValue(
    pointsRef,
    (snapshot) => {
      const fetchedData = [];
      snapshot.forEach((childSnapshot) => {
        const rawPoint = childSnapshot.val().cycle_analyst; // Données brutes
        if (rawPoint !== undefined) {
          const formattedPoint = formatPoint(rawPoint); // Formatter le point
          
          fetchedData.push(formattedPoint);
        }
      });

      if (fetchedData.length > 0) {
        onSuccess(fetchedData[fetchedData.length - 1]);
      }
    },
    (error) => {
      console.error('Erreur lors de la récupération du dernier point :', error);
      if (onError) onError(error);
    }
  );

  return unsubscribe;
};


export const fetchAllCollections = (onSuccess, onError) => {
  const rootRef = ref(db, "/");

  const unsubscribe = onValue(
    rootRef,
    (snapshot) => {
      const fetchedData = [];
      snapshot.forEach((childSnapshot) => {
        if(childSnapshot.key !== "parametres"){
          fetchedData.push(childSnapshot.key); // On récupère uniquement les clés (noms des collections)
        }
      });
      if(localStorage.getItem('collectionName') === null || localStorage.getItem('collectionName') === "undefined"){
        localStorage.setItem('collectionName', fetchedData[0]);
      }
      onSuccess(fetchedData); // On passe les collections à la fonction de succès
    },
    (error) => {
      if (onError) onError(error);
    }
  );

  return unsubscribe;
};


export const formatGPS = (gpsData) => {
  const gpsPoints = [];
  let lastAltitude = null; // Stocker la dernière altitude connue

  gpsData.forEach(data => {
      let latitude = null, longitude = null, altitude = null;

      // Extraction des données GPGGA pour l'altitude uniquement
      const gpggaMatch = data.match(/\$GPGGA,\d+\.\d+,\d{2}\d+\.\d+,[NS],\d{3}\d+\.\d+,[EW],\d+,\d+,\d+\.\d+,(\d+\.\d+),/);
      if (gpggaMatch) {
          lastAltitude = parseFloat(gpggaMatch[1]); // Stocker la dernière altitude connue
      }
      
      // Extraction des données GPRMC qui contiennent la latitude et la longitude
      const gprmcMatch = data.match(/\$GPRMC,\d+\.\d+,A,(\d{2})(\d+\.\d+),([NS]),(\d{3})(\d+\.\d+),([EW])/);
      if (gprmcMatch) {
          let latDeg = parseFloat(gprmcMatch[1]);
          let latMin = parseFloat(gprmcMatch[2]);
          let lonDeg = parseFloat(gprmcMatch[4]);
          let lonMin = parseFloat(gprmcMatch[5]);

          // Conversion coordonnées DD (degrés décimaux)
          latitude = (latDeg + latMin / 60) * (gprmcMatch[3] === 'S' ? -1 : 1);
          longitude = (lonDeg + lonMin / 60) * (gprmcMatch[6] === 'W' ? -1 : 1);

          // Associer l'altitude si elle est connue
          altitude = lastAltitude;

          gpsPoints.push({ latitude, longitude, altitude });
      }
  });

  return gpsPoints;
};